import {
    mdiClose,
    mdiMenu,
} from '@mdi/js'
import clsx from 'clsx'
import {
    type MouseEvent,
    useEffect,
    useState,
} from 'react'
import {FadeSwitch} from 'swayok-react-mdb-ui-kit/components/Animation/FadeSwitch'
import {Dropdown} from 'swayok-react-mdb-ui-kit/components/Dropdown/Dropdown'
import {DropdownMenu} from 'swayok-react-mdb-ui-kit/components/Dropdown/DropdownMenu'
import {DropdownToggle} from 'swayok-react-mdb-ui-kit/components/Dropdown/DropdownToggle'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {Modal} from 'swayok-react-mdb-ui-kit/components/Modal/Modal'
import {ModalBody} from 'swayok-react-mdb-ui-kit/components/Modal/ModalBody'
import {ModalContent} from 'swayok-react-mdb-ui-kit/components/Modal/ModalContent'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import {BottomDrawerCloseIndicator} from '../../../components/BottomDrawerCloseIndicator'
import {UserAvatar} from '../../../components/UserAvatar'
import {useUserData} from '../../../hooks'
import {useUiLayoutStore} from '../../../stores'
import {TopNavigationUserDropdownMenuLinks} from './TopNavigationUserDropdownMenuLinks'
import {TopNavigationUserDropdownMenuUserInfo} from './TopNavigationUserDropdownMenuUserInfo'

interface TopNavigationUserDropdownMenuProps {
    className?: string
}

// Иконка профиля пользователя с выпадающем меню в заголовке страницы.
export function TopNavigationUserDropdownMenu(props: TopNavigationUserDropdownMenuProps) {

    const {
        className,
    } = props

    const [
        isOpened,
        setIsOpened,
    ] = useState<boolean>(false)

    const user = useUserData()

    const bootstrapSize = useUiLayoutStore(
        state => state.bootstrapSize
    )

    const deviceType = useUiLayoutStore(
        state => state.deviceType
    )

    const isDrawer: boolean = bootstrapSize === 'xxs' || bootstrapSize === 'xs'

    useEffect(() => {
        setIsOpened(false)
    }, [isDrawer])

    // Закрытие меню при переходе на другую страницу.
    useEffect(() => {
        NavigationService.registerNavigationCallback('top-nav-user-dropdown-menu', () => {
            setIsOpened(false)
        })
        return () => {
            NavigationService.removeNavigationCallback('top-nav-user-dropdown-menu')
        }
    }, [])

    useEffect(() => {
        if (!isOpened) {
            document.body.style.overflow = ''
        }
    }, [isOpened])

    const closeDrawerOnItemClick = (event: MouseEvent<HTMLElement>) => {
        if ((event.target as HTMLElement).closest('.dropdown-item')) {
            setIsOpened(false)
        }
    }

    let menuOffsetX: number = 0
    let addPublicLinksToMenu: boolean = false
    switch (bootstrapSize) {
        case 'xl':
        case 'xxl':
            menuOffsetX = -10
            break
        case 'md':
        case 'sm':
        case 'xs':
        case 'xxs':
            addPublicLinksToMenu = true
            break
        default:
            break
    }

    const toggleContent = (
        <UserAvatar
            small
            userName={user?.fullName ?? null}
            fallbackContent={(
                <FadeSwitch
                    transitionKey={isOpened}
                    animation="page-switch"
                >
                    <Icon
                        path={isOpened ? mdiClose : mdiMenu}
                        color="gray"
                        size={24}
                    />
                </FadeSwitch>
            )}
            fallbackContentClassName={clsx(
                'with-icon',
                deviceType === 'mobile' || bootstrapSize === 'md'
                    ? 'bg-light-gray'
                    : 'bg-transparent'
            )}
            forceFallbackContent={!!user && isOpened}
        />
    )

    if (isDrawer) {
        return (
            <>
                <div
                    className={clsx('cursor py-2', className)}
                    onClick={() => setIsOpened(!isOpened)}
                >
                    {toggleContent}
                </div>
                <Modal
                    show={isOpened}
                    onClose={() => setIsOpened(false)}
                    className="bottom-drawer top-nav-user-menu-drawer"
                >
                    <ModalContent>
                        <BottomDrawerCloseIndicator onClose={() => setIsOpened(false)} />
                        <ModalBody
                            className="pt-0"
                            onClick={closeDrawerOnItemClick}
                        >
                            <TopNavigationUserDropdownMenuUserInfo />
                            <TopNavigationUserDropdownMenuLinks forMobile />
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </>
        )
    }

    return (
        <Dropdown
            focusFirstItemOnOpen={false}
            open={isOpened}
            onOpenChange={setIsOpened}
        >
            <DropdownToggle
                tag="div"
                className={clsx('cursor py-2', className)}
            >
                {toggleContent}
            </DropdownToggle>
            <DropdownMenu
                shadow="2-strong"
                shift={false}
                flip={false}
                drop="down"
                align="end"
                offset={{
                    mainAxis: 6,
                    alignmentAxis: menuOffsetX,
                }}
                fixedPositioning
                noFocus
                id="top-nav-user-dropdown-menu"
                className="rounded-8"
            >
                <TopNavigationUserDropdownMenuUserInfo />
                <TopNavigationUserDropdownMenuLinks
                    forMobile={addPublicLinksToMenu}
                />
            </DropdownMenu>
        </Dropdown>
    )
}
